





















































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { FeedTankLegShapes, FeedTankLegShapesEnum } from '@/api';
import LegShapeGuideModal from './LegShapeGuideModal.vue';
import { FeedProductService } from '@/pages/account/products/feed/services';
import {
	SensorType,
	SENSOR_OPTIONS,
	isRadar,
	isStrain,
} from '@/pages/account/products/feed/models/sensor-config';
import Toast from '@/components/display/toast.vue';
import { FormSelectOption } from '@/models/form-select-option';

interface SensorConfig {
	label: string;
	unit: string;
}

const SENSOR_CONFIG: Record<SensorType, SensorConfig> = {
	strain: { label: 'Nominal Weight', unit: 't' },
	radar: { label: 'Nominal Volume', unit: 'L' },
};

@Component({ components: { LegShapeGuideModal } })
export default class AddFeedTankModal extends Vue {
	@Prop({ required: true, type: Function })
	private createFeedTank!: (params: {
		nominalVolume?: number;
		nominalWeight?: number;
		name: string;
		sensorType: string;
		numLegs?: number | null;
		legShape?: FeedTankLegShapesEnum | null;
		brand?: string | null;
	}) => Promise<void>;

	@Prop({ required: true, type: Function })
	private onTankCreated!: () => Promise<void>;

	public name = '';
	public nominalValue = 0;
	public sensorType: SensorType = 'strain';
	public numLegs: number | null = null;
	public legShape: FeedTankLegShapesEnum | null = null;
	public brand: string | null = null;

	public legShapeOptions: FormSelectOption[] = [{ value: null, text: 'Select leg shape (optional)' }];
	private readonly toast = new Toast();
	private readonly service = new FeedProductService();

	public readonly sensorOptions = SENSOR_OPTIONS;

	public get isStrainSensor(): boolean {
		return isStrain(this.sensorType);
	}

	// Computed from config
	public get config(): SensorConfig {
		return SENSOR_CONFIG[this.sensorType];
	}

	public get nominalLabel(): string {
		return this.config.label;
	}

	public get unit(): string {
		return this.config.unit;
	}

	public get placeholder(): string {
		return `Enter a nominal ${isRadar(this.sensorType) ? 'volume' : 'weight'}`;
	}

	public get inputStep(): string {
		return isRadar(this.sensorType) ? '1' : '0.1';
	}

	public get validationMessage(): string {
		if (isRadar(this.sensorType)) {
			return `${this.nominalLabel} is required and must be a whole number greater than or equal to 0${this.unit}`;
		}
		return `${this.nominalLabel} is required and must be greater than or equal to 0${this.unit} (max 1 decimal place)`;
	}

	public get nameValid(): boolean {
		return this.name.length > 0;
	}

	public get nominalValueValid(): boolean {
		if (typeof this.nominalValue !== 'number' || this.nominalValue < 0) {
			return false;
		}
		if (isRadar(this.sensorType)) {
			return Number.isInteger(this.nominalValue);
		} else {
			return parseFloat(this.nominalValue.toFixed(1)) === this.nominalValue;
		}
	}

	public get numLegsValid(): boolean | null {
		if (!Number.isFinite(this.numLegs as any)) return null;
		return Number.isInteger(this.numLegs) && (this.numLegs as number) >= 3 && (this.numLegs as number) <= 99;
	}

	public get canSubmit(): boolean {
		const numLegsOk = this.isStrainSensor ? this.numLegsValid !== false : true;
		return this.nominalValueValid && this.nameValid && numLegsOk;
	}

	public async created(): Promise<void> {
		const response = await this.service.getFeedTankLegShapes();
		if (response.legShapes) {
			this.legShapeOptions = [
				{ value: null, text: 'Select leg shape (optional)' },
				...response.legShapes.map((s: FeedTankLegShapes) => ({ value: s.value, text: s.label })),
			];
		}
	}

	public resetForm(): void {
		this.name = '';
		this.nominalValue = 0;
		this.sensorType = 'strain';
		this.numLegs = null;
		this.legShape = null;
		this.brand = null;
	}

	public async onSave(): Promise<void> {
		// Convert tons to kg for strain (1 ton = 1000 kg)
		const nominalWeightInKg = this.nominalValue * 1000;

		const params = isRadar(this.sensorType)
			? { nominalVolume: this.nominalValue, name: this.name, sensorType: 'radar' }
			: { nominalWeight: nominalWeightInKg, name: this.name, sensorType: 'strain' };

		await this.createFeedTank({
			...params,
			numLegs: this.isStrainSensor ? this.numLegs || null : null,
			legShape: this.isStrainSensor ? this.legShape || null : null,
			brand: this.isStrainSensor ? this.brand || null : null,
		});
		await this.onTankCreated();
		this.resetForm();
	}
}
